<script setup lang="ts">
import { cn } from '~~/lib/tw-utils'
import { X } from 'lucide-vue-next'
import {
  DialogClose,
  DialogContent,
  type DialogContentEmits,
  type DialogContentProps,
  DialogOverlay,
  DialogPortal,
  useForwardPropsEmits,
} from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

const props = withDefaults(
  defineProps<DialogContentProps & { class?: HTMLAttributes['class']; hideClose?: boolean; overlayClass?: HTMLAttributes['class'] }>(),
  {
    class: '',
    hideClose: false,
    overlayClass: '',
  },
)
const emits = defineEmits<DialogContentEmits & { close: [] }>()

const delegatedProps = computed(() => {
  const { class: _, ...delegated } = props

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <DialogPortal>
    <DialogOverlay
      :class="
        cn(
          'fixed inset-0 z-50 bg-black/80 data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0',
          props.overlayClass,
        )
      "
    />
    <DialogContent
      v-bind="forwarded"
      :class="
        cn(
          'fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 border text-foreground bg-background p-6 shadow-lg duration-200 data-[state=open]_animate-in data-[state=closed]_animate-out data-[state=closed]_fade-out-0 data-[state=open]_fade-in-0 data-[state=closed]_zoom-out-95 data-[state=open]_zoom-in-95 data-[state=closed]_slide-out-to-left-1/2 data-[state=closed]_slide-out-to-top-[48%] data-[state=open]_slide-in-from-left-1/2 data-[state=open]_slide-in-from-top-[48%] sm_rounded-lg',
          props.class,
        )
      "
    >
      <slot />

      <DialogClose
        v-if="!hideClose"
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover_opacity-100 focus_outline-none focus_ring-2 focus_ring-ring focus_ring-offset-2 disabled_pointer-events-none data-[state=open]_bg-accent data-[state=open]_text-muted-foreground"
        @click="emits('close')"
      >
        <X class="w-4 h-4" />
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
